import { useCallback, useEffect, useMemo, useState } from 'react';
import { Field, useField } from 'formik';
import { Badge, Flex, Spinner } from '@chakra-ui/react';

import { useUnitsAndMeasures } from 'newui/store';
import { RfxQuoteHeader } from 'newui/features/Rfx/Submission/components/QuoteTab/RfxQuoteHeader';
import { QuoteTableView } from './QuoteTableView';
import { formatCurrency, getQuoteDiff, mapSnapshotToRows } from './utils';
import type { QuoteTableRow } from './quote-table.types';
import type { RfxQuoteTable } from '../../types/quote.type';
import type { QuoteRevisionSnapshot } from '../../types/revision.type';
import { RFX_SUBMISSION_STATUS } from '../../constants';
import { getQuoteRevisions } from '../../api';
import { UpdateQuoteModal } from './UpdateQuoteModal';
import { CompareRevisionsModal } from './CompareRevisionsModal';
import { LoadingSpinner } from 'components/Loading/LoadingSpinner';

export interface QuoteTabProps {
    disabled: boolean;
    inviteStatus: string;
    isClientView: boolean;
    listQuotes: RfxQuoteTable[];
    rfxInviteId?: number;
    onDraftRevisionChange?: (hasDraft: boolean) => void;
    onQuoteRevisionUpdated?: () => void;
}

interface QuoteFieldValues {
    [key: string]: {
        cost?: number | string;
        quantity: number | string;
        rate: number | string;
        unit: number | string;
    };
}

const TABLE_HEADERS = [
    { name: '#', width: '3%' },
    { name: 'Description of Scope', width: '40%' },
    { name: 'Unit of Measurement', width: '19%' },
    { name: 'Quantity', width: '12%' },
    { name: 'Rate', width: '12%' },
    { name: 'Cost', width: '12%' },
];

const TOTAL_ROWS = [
    { id: 'subtotal', name: 'Subtotal', color: '#475467' },
    { id: 'gst', name: 'GST(10%)', color: '#475467' },
    { id: 'totalcost', name: 'Total Cost', color: '#101828' },
] as const;

type TotalType = (typeof TOTAL_ROWS)[number]['id'];

const calculateTotal = (
    type: TotalType,
    keys: string[] | 'all',
    allValue: QuoteFieldValues
): number => {
    const keyIds =
        keys === 'all' ? Object.keys(allValue ?? {}) : keys;
    const subTotal = keyIds.reduce((acc, key) => {
        const rate = Number(allValue?.[key]?.rate ?? 0);
        const quantity = Number(allValue?.[key]?.quantity ?? 0);
        return acc + rate * quantity;
    }, 0);

    switch (type) {
        case 'subtotal':
            return subTotal;
        case 'gst':
            return subTotal * 0.1;
        case 'totalcost':
            return subTotal + subTotal * 0.1;
        default:
            return 0;
    }
};

const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === '-') {
        event.preventDefault();
    }
};

const handleNumberValue = (value: string | number): number | string => {
    const numericValue = parseFloat(String(value));
    if (isNaN(numericValue) || numericValue < 0) {
        return '';
    }
    return numericValue;
};

const QuoteTab: React.FC<QuoteTabProps> = ({
    isClientView,
    listQuotes,
    disabled,
    inviteStatus,
    rfxInviteId,
    onDraftRevisionChange,
    onQuoteRevisionUpdated,
}) => {
    const [, { value: values }] = useField<QuoteFieldValues>('quote_answer');
    const { isLoading: isUnitMeasuresLoading, values: unitsAndMeasures } = useUnitsAndMeasures();
    const [isQuotesLoading, setIsLoading] = useState(true);
    const [showUpdateQuoteModal, setShowUpdateQuoteModal] = useState(false);
    const [updateQuoteData, setUpdateQuoteData] = useState<{
        current: null | QuoteRevisionSnapshot;
        previous: null | QuoteRevisionSnapshot;
        quote: null | RfxQuoteTable;
    }>({ current: null, previous: null, quote: null });
    const [showCompareModal, setShowCompareModal] = useState(false);
    const [compareQuote, setCompareQuote] = useState<RfxQuoteTable | null>(null);
    const [draftRevisions, setDraftRevisions] = useState<Record<number, QuoteRevisionSnapshot>>({});
    const [quoteDiffKeys, setQuoteDiffKeys] = useState<Record<number, Set<string>>>({});

    const isBeingRevised = inviteStatus === RFX_SUBMISSION_STATUS.BEING_REVISED;

    // Fetch draft revisions per-quote when in BEING_REVISED status
    const fetchDraftRevisions = useCallback(async () => {
        if (!isBeingRevised || !rfxInviteId || !listQuotes?.length) {
            setDraftRevisions({});
            setIsLoading(false);
            return;
        }
        try {
            const results: Record<number, QuoteRevisionSnapshot> = {};
            await Promise.all(
                listQuotes.map(async (quote) => {
                    const revisions = await getQuoteRevisions({ inviteId: rfxInviteId, quoteId: quote.id });
                    const draft = revisions.find((r) => !r.is_submitted && r.revision > 0);
                    if (draft) {
                        results[quote.id] = draft;
                    }
                })
            );
            setDraftRevisions(results);
        } catch {
            setDraftRevisions({});
        } finally {
            setIsLoading(false);
        }
    }, [isBeingRevised, rfxInviteId, listQuotes]);

    useEffect(() => {
        setIsLoading(true);
        fetchDraftRevisions();
    }, [fetchDraftRevisions]);

    const hasDrafts = Object.keys(draftRevisions).length > 0;

    useEffect(() => {
        onDraftRevisionChange?.(hasDrafts);
    }, [hasDrafts, onDraftRevisionChange]);

    // Fetch previous revision diffs for read-only view when quotes have revisions
    useEffect(() => {
        if (!disabled || !rfxInviteId || !listQuotes?.length) {
            return;
        }

        const quotesWithRevisions = listQuotes.filter((q) => q.has_revisions && q.current_revision > 0);
        if (quotesWithRevisions.length === 0) {
            return;
        }

        const fetchDiffs = async () => {
            const diffs: Record<number, Set<string>> = {};
            await Promise.all(
                quotesWithRevisions.map(async (quote) => {
                    try {
                        const revisions = await getQuoteRevisions({ inviteId: rfxInviteId, quoteId: quote.id });
                        const sorted = [...revisions].sort((a, b) => b.revision - a.revision);
                        const current = isClientView ? sorted.find((r) => r.is_submitted) : sorted[0];
                        const previous = sorted.find((r) => r.is_submitted && r.revision < (current?.revision ?? 0));
                        if (current && previous) {
                            diffs[quote.id] = getQuoteDiff(current, previous);
                        }
                    } catch {
                        // Error handled by global interceptor
                    }
                })
            );
            setQuoteDiffKeys(diffs);
        };

        fetchDiffs();
    }, [disabled, rfxInviteId, listQuotes, isClientView]);

    const allItemKeys = useMemo(
        () =>
            listQuotes?.flatMap((quote) =>
                quote.items.map((item) => `quote-${item.id}`)
            ) ?? [],
        [listQuotes]
    );

    // Compute overall totals, substituting draft revision figures when applicable
    const overallTotals = useMemo(() => {
        let subtotal = 0;

        listQuotes?.forEach((quote) => {
            const draftRevision = draftRevisions[quote.id];
            if (draftRevision && isBeingRevised && !isClientView) {
                const rows = mapSnapshotToRows(draftRevision, quote.items);
                subtotal += rows.reduce((acc, row) => acc + row.quantity * row.rate, 0);
            } else {
                quote.items.forEach((item) => {
                    const key = `quote-${item.id}`;
                    const rate = Number(values?.[key]?.rate ?? 0);
                    const quantity = Number(values?.[key]?.quantity ?? 0);
                    subtotal += rate * quantity;
                });
            }
        });

        return {
            subtotal,
            gst: subtotal * 0.1,
            totalcost: subtotal + subtotal * 0.1,
        };
    }, [listQuotes, draftRevisions, isBeingRevised, isClientView, values]);

    const createRevision = (snapshot: QuoteRevisionSnapshot) => {
        const newRevision = snapshot.revision + 1;
        return { ...snapshot, label: `Revision ${newRevision}`, is_submitted: false, revision: newRevision };
    }

    const handleOpenReviseQuote = useCallback(async (quote: RfxQuoteTable) => {
        if (!rfxInviteId) {
            return;
        }
        try {
            const revisions = await getQuoteRevisions({ inviteId: rfxInviteId, quoteId: quote.id });
            const sorted = [...revisions].sort((a, b) => b.revision - a.revision);

            const isLatestSubmitted = sorted[0].is_submitted;

            // if latest revision has been submitted, we need a new current submission
            const currentRevision = isLatestSubmitted ? createRevision(sorted[0]) : sorted[0];

            // if latest has been submitted, its the previous, otherwise its the one before this 
            const previousRevision = isLatestSubmitted ? sorted[0] : sorted[1];

            if (currentRevision && previousRevision) {
                setUpdateQuoteData({ current: currentRevision, previous: previousRevision, quote });
                setShowUpdateQuoteModal(true);
            }
        } catch {
            // Error handled by global interceptor
        }
    }, [rfxInviteId]);

    const buildReadOnlyRows = (items: RfxQuoteTable['items']): QuoteTableRow[] =>
        items.map((item) => {
            const key = `quote-${item.id}`;
            const unitLabel = unitsAndMeasures?.find(
                (u) => u.value === Number(values?.[key]?.unit ?? 0)
            )?.label;
            return {
                itemId: item.id,
                name: item.name || '-',
                unitLabel,
                quantity: Number(values?.[key]?.quantity ?? 0),
                rate: Number(values?.[key]?.rate ?? 0),
            };
        });

    if (isQuotesLoading) {
        return <LoadingSpinner />
    }

    return (
        <>
            {showUpdateQuoteModal && updateQuoteData.current && updateQuoteData.previous && updateQuoteData.quote && (
                <UpdateQuoteModal
                    isOpen={showUpdateQuoteModal}
                    onClose={() => setShowUpdateQuoteModal(false)}
                    inviteId={rfxInviteId}
                    currentData={updateQuoteData.current}
                    previousData={updateQuoteData.previous}
                    quote={updateQuoteData.quote}
                    onSaveSuccess={() => {
                        onQuoteRevisionUpdated?.();
                        fetchDraftRevisions();
                    }}
                />
            )}
            {showCompareModal && compareQuote && rfxInviteId && (
                <CompareRevisionsModal
                    isOpen={showCompareModal}
                    onClose={() => setShowCompareModal(false)}
                    inviteId={rfxInviteId}
                    quoteId={compareQuote.id}
                    quoteName={compareQuote.name}
                    quoteItems={compareQuote.items}
                    initialRevisionA={compareQuote.current_revision}
                    initialRevisionB={compareQuote.current_revision > 0 ? compareQuote.current_revision - 1 : 0}

                />
            )}
            {listQuotes?.map((quote) => {
                const draftRevision = draftRevisions[quote.id];
                const totalRevisions = draftRevision
                    ? draftRevision.revision
                    : quote.current_revision;

                return (
                    <RfxQuoteHeader
                        key={`quote-section-${quote.id}`}
                        isBeingRevised={!isClientView && isBeingRevised}
                        hasRevisions={quote.has_revisions}
                        onOpenReviseQuote={handleOpenReviseQuote}
                        onCompareRevisions={(q) => {
                            setCompareQuote(q);
                            setShowCompareModal(true);
                        }}
                        quote={quote}
                    >
                        {quote.has_revisions && !draftRevision && (
                            <Flex
                                className="mb-3"
                                align="center"
                                gap={2}
                            >
                                <Badge
                                    colorScheme="blue"
                                    data-testid={`revision-badge-${quote.id}`}
                                >
                                    Revision {quote.current_revision} of {totalRevisions}
                                </Badge>
                            </Flex>
                        )}
                        {draftRevision && isBeingRevised && (
                            <Flex
                                className="mb-3"
                                align="center"
                                gap={2}
                            >
                                <Badge
                                    colorScheme="orange"
                                    data-testid={`draft-revision-badge-${quote.id}`}
                                >
                                    Revision {draftRevision.revision}
                                </Badge>
                            </Flex>
                        )}
                        {draftRevisions[quote.id] && isBeingRevised && disabled ? (
                            <QuoteTableView
                                rows={mapSnapshotToRows(draftRevisions[quote.id], quote.items)}
                                diffKeys={quoteDiffKeys[quote.id]}
                                showUnitColumn
                                showAllTotals
                            />
                        ) : disabled ? (
                            <QuoteTableView
                                rows={buildReadOnlyRows(quote.items)}
                                diffKeys={quoteDiffKeys[quote.id]}
                                showUnitColumn
                                showAllTotals
                            />
                        ) : (
                            <table className="w-full table-auto rounded-xl border-b border-solid border-[#EAECF0] bg-white">
                                <thead className="h-[44px] bg-[#F9FAFB]">
                                    <tr className="border-b border-[#EAECF0]">
                                        {TABLE_HEADERS.map((th) => (
                                            <th
                                                key={th.name}
                                                className="bg-[#F9FAFB] text-left font-roboto text-[12px] font-medium text-tertiary-600"
                                                style={{ width: th.width }}
                                            >
                                                <div className="ml-5 mt-[3px]">{th.name}</div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {quote?.items.map((singleData, itemIndex) => {
                                        const isPercentage = singleData.unit_id === 13;
                                        return <tr
                                            key={`quote-item-${singleData.id}`}
                                            className="border-b border-[#EAECF0]"
                                        >
                                            <td
                                                className="border border-[#EAECF0] px-4 py-2 text-center font-roboto font-normal text-primary-500"
                                                style={{ background: '#F9FAFB', height: '48px' }}
                                            >
                                                {itemIndex + 1}
                                            </td>
                                            <td
                                                className="relative border border-[#EAECF0] font-roboto"
                                                style={{ color: '#333', backgroundColor: '#fafafa' }}
                                            >
                                                <Field
                                                    name={`quote_answer[quote-${singleData.id}][name]`}
                                                >
                                                    {() => (
                                                        <p className="p-2">{singleData?.name || '-'}</p>
                                                    )}
                                                </Field>
                                            </td>
                                            <td
                                                className="relative border border-[#EAECF0] font-roboto"
                                                style={{ color: '#333', backgroundColor: '#fafafa' }}
                                            >
                                                {isUnitMeasuresLoading && (
                                                    <Spinner
                                                        size="sm"
                                                        className="ml-2 mt-1"
                                                    />
                                                )}
                                                {!isUnitMeasuresLoading && (
                                                    <Field
                                                        name={`quote_answer[quote-${singleData.id}][unit]`}
                                                    >
                                                        {({ field }: { field: { value: number } }) => (
                                                            <p className="px-2">
                                                                {
                                                                    unitsAndMeasures?.find(
                                                                        (unit) => unit.value === field?.value
                                                                    )?.label
                                                                }
                                                            </p>
                                                        )}
                                                    </Field>
                                                )}
                                            </td>
                                            <td
                                                className="relative border border-[#EAECF0] px-4 py-2 font-roboto"
                                                style={{ color: '#333' }}
                                            >
                                                <Field
                                                    name={`quote_answer[quote-${singleData.id}][quantity]`}
                                                >
                                                    {({
                                                        field,
                                                        meta,
                                                        form,
                                                    }: {
                                                        field: React.InputHTMLAttributes<HTMLInputElement> & { name: string };
                                                        form: { setFieldValue: (name: string, value: number | string) => void };
                                                        meta: { error?: string; touched: boolean; };
                                                    }) => (
                                                        <input
                                                            type="number"
                                                            {...field}
                                                            value={handleNumberValue(field.value as string)}
                                                            onChange={(e) =>
                                                                form.setFieldValue(
                                                                    field.name,
                                                                    handleNumberValue(e.target.value)
                                                                )
                                                            }
                                                            className="absolute left-0 top-0 h-full w-full px-5 py-[5px] outline-none"
                                                            style={{
                                                                border:
                                                                    meta.touched && meta.error
                                                                        ? '2px solid #dd0c0cb3'
                                                                        : undefined,
                                                            }}
                                                            placeholder="0"
                                                            min={0}
                                                            onKeyDown={handleKeyDown}
                                                            disabled={disabled}
                                                        />
                                                    )}
                                                </Field>
                                            </td>
                                            <td
                                                className="relative border border-[#EAECF0] px-4 py-2 font-roboto"
                                                style={{ color: '#333' }}
                                            >
                                                <div className="flex">
                                                    {!isPercentage && <div className="absolute left-2 top-1/2 z-10 -translate-y-1/2">
                                                        $
                                                    </div>}
                                                    <Field
                                                        name={`quote_answer[quote-${singleData.id}][rate]`}
                                                    >
                                                        {({
                                                            field,
                                                            meta,
                                                            form,
                                                        }: {
                                                            field: React.InputHTMLAttributes<HTMLInputElement> & { name: string };
                                                            form: { setFieldValue: (name: string, value: number | string) => void };
                                                            meta: { error?: string; touched: boolean; };
                                                        }) => (
                                                            isPercentage ? <></> :
                                                                <input
                                                                    type="number"
                                                                    {...field}
                                                                    value={handleNumberValue(field.value as string)}
                                                                    onChange={(e) =>
                                                                        form.setFieldValue(
                                                                            field.name,
                                                                            handleNumberValue(e.target.value)
                                                                        )
                                                                    }
                                                                    className="absolute left-0 right-0 top-0 h-full w-full px-5 py-[5px] pl-5 outline-none"
                                                                    style={{
                                                                        border:
                                                                            meta.touched && meta.error
                                                                                ? '2px solid #dd0c0cb3'
                                                                                : undefined,
                                                                    }}
                                                                    placeholder="0"
                                                                    min={0}
                                                                    onKeyDown={handleKeyDown}
                                                                    disabled={disabled}
                                                                />
                                                        )}
                                                    </Field>
                                                </div>
                                            </td>
                                            <td
                                                className="relative border border-[#EAECF0] px-4 py-2 font-roboto"
                                                style={{ color: '#333' }}
                                            >
                                                <Field
                                                    name={`quote_answer[quote-${singleData.id}][cost]`}
                                                    disabled={true}
                                                >
                                                    {() => (
                                                        isPercentage ? '-' :
                                                            <input
                                                                type="text"
                                                                readOnly
                                                                disabled
                                                                className="absolute left-0 top-0 h-full w-full px-5 py-[5px]"
                                                                value={formatCurrency(
                                                                    (Number(
                                                                        values?.[`quote-${singleData.id}`]
                                                                            ?.quantity ?? 0
                                                                    ) *
                                                                        Number(
                                                                            values?.[`quote-${singleData.id}`]
                                                                                ?.rate ?? 0
                                                                        ))
                                                                )}
                                                            />
                                                    )}
                                                </Field>
                                            </td>
                                        </tr>
                                    })}
                                    {TOTAL_ROWS.map((list) => (
                                        <tr key={list.id}>
                                            <td
                                                colSpan={isBeingRevised ? 5 : 4}
                                                className="h-[48px] border-none"
                                                style={{ background: '#F9FAFB' }}
                                            />
                                            <td
                                                className="border border-[#EAECF0] bg-gray-utility-100 px-4 py-2"
                                                style={{ color: '#D0D5DD' }}
                                            >
                                                <div
                                                    className="font-roboto font-medium"
                                                    style={{ fontSize: '12px', color: list.color }}
                                                >
                                                    {list.name}
                                                </div>
                                            </td>
                                            <td
                                                className="border border-[#EAECF0] bg-gray-utility-100 px-4 py-2"
                                                style={{ color: '#333' }}
                                            >
                                                {formatCurrency(
                                                    calculateTotal(
                                                        list.id,
                                                        quote?.items.map((item) => `quote-${item.id}`),
                                                        values
                                                    )
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </RfxQuoteHeader>
                );
            })}
            <div className="mb-10 flex h-[64px] items-center justify-start overflow-hidden rounded-xl border border-solid border-[#EAECF0] bg-white shadow-lg">
                <div className="h-full content-center border-l-4 border-[#003CE9] pl-10 font-roboto text-[18px] font-semibold text-primary-900">
                    Total
                </div>
                <div className="ml-auto h-full content-center border-l border-solid border-[#EAECF0] pl-6 pr-20">
                    <div className="font-roboto text-[12px] font-medium text-primary-900">
                        Subtotal
                    </div>
                    <div
                        className="font-roboto font-medium"
                        style={{ color: '#D0D5DD' }}
                    >
                        {formatCurrency(overallTotals.subtotal)}
                    </div>
                </div>
                <div className="h-full content-center border-l border-solid border-[#EAECF0] pl-6 pr-20">
                    <div className="font-roboto text-[12px] font-medium text-primary-900">
                        GST (10%)
                    </div>
                    <div
                        className="font-roboto font-medium"
                        style={{ color: '#D0D5DD' }}
                    >
                        {formatCurrency(overallTotals.gst)}
                    </div>
                </div>
                <div className="h-full content-center border-solid border-[#EAECF0] bg-royal-blue2 pl-6 pr-20">
                    <div
                        className="font-roboto text-[12px] font-medium"
                        style={{ color: '#D0D5DD' }}
                    >
                        Total Cost
                    </div>
                    <div className="font-roboto font-medium text-white">
                        {formatCurrency(overallTotals.totalcost)}
                    </div>
                </div>
            </div>
        </>
    );
};

export { QuoteTab };
